import { useEffect, useMemo } from 'react'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { EnrichedTask } from '@/lib/db/rxdb/hooks/entities/tasks'
import type {Density} from '@/store/ProjectViewPreferencesStore';
import { useChildProjects, useRootProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import useProjectViewPreferencesStore from '@/store/ProjectViewPreferencesStore'
import useProjectFilterStore from '@/store/ProjectFilterStore'
import { usePageLayout } from '@/hooks/usePageLayout'
import { useMultiProjectTasks } from '@/routes/features/tasks/hooks/useMultiProjectTasks'
import { EMPTY_FILTERS } from '@/lib/types/filter'
import useViewModeStore from '@/store/ViewModeStore'

interface SpaceViewPageSetup {
  space: ProjectDocument | null
  spaceLoading: boolean
  childProjectIds: Array<string>
  topLevelTasks: Array<EnrichedTask>
  /** What a list shows: top-level tasks, plus sub-tasks under Display → Show sub-tasks (28b). */
  listTasks: Array<EnrichedTask>
  isLoading: boolean
  density: Density
}

export function useSpaceViewPageSetup(
  _workspaceSlug: string,
  spaceKey: string,
  renderBreadcrumbActions: (spaceId: string) => React.ReactNode,
  options?: { includeCompleted?: boolean },
): SpaceViewPageSetup {
  const workspaceId = useLoginStore((s) => s.workspaceId)
  const { projects: rootProjects, isLoading: spacesLoading } = useRootProjects()

  const space = useMemo<ProjectDocument | null>(() => {
    return (
      rootProjects?.find((entry) => {
        if (entry.isDeleted) return false
        if (entry.projectKey.toLowerCase() !== spaceKey.toLowerCase()) return false
        if (workspaceId && entry.envId !== workspaceId) return false
        return true
      }) ?? null
    )
  }, [rootProjects, spaceKey, workspaceId])

  const { projects: childProjects } = useChildProjects(space?.id)

  const childProjectIds = useMemo(
    () => (childProjects ?? []).filter((p) => !p.isDeleted).map((p) => p.id),
    [childProjects],
  )

  // Ensure pinned project filter exists
  const upsertFilters = useProjectFilterStore((s) => s.upsertFilters)
  const existingFilters = useProjectFilterStore(
    (s) => s.filters[space?.id ?? ''],
  )

  useEffect(() => {
    if (!space || childProjectIds.length === 0) return
    // Pinned: ungrouped, so it applies to every OR group.
    const hasProjectFilter = existingFilters?.some(
      (f) => f.fieldKey === 'project' && f.group === undefined,
    )
    if (!hasProjectFilter) {
      upsertFilters(space.id, [
        { fieldKey: 'project', operator: 'includes', values: childProjectIds },
      ])
    }
  }, [space?.id, childProjectIds, existingFilters, upsertFilters])

  const filters = useProjectFilterStore(
    (s) => s.filters[space?.id ?? ''] ?? EMPTY_FILTERS,
  )
  const { tasks, topLevelTasks, isLoading: tasksLoading } = useMultiProjectTasks(
    childProjectIds,
    filters,
    { includeCompleted: options?.includeCompleted ?? false },
  )

  const showSubTasks = useViewModeStore((s) => s.showSubTasks)
  const listTasks = showSubTasks ? tasks : topLevelTasks

  const density = useProjectViewPreferencesStore(
    (s) => (space ? s.preferences[space.id]?.density : undefined) ?? 'simple',
  )

  const breadcrumbActions = useMemo(() => {
    if (!space) return null
    return renderBreadcrumbActions(space.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [space?.id])

  usePageLayout({ breadcrumbActions }, [space?.id])

  return {
    space,
    spaceLoading: spacesLoading,
    childProjectIds,
    topLevelTasks,
    listTasks,
    isLoading: tasksLoading,
    density,
  }
}
